'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate, timelineDateLabel } from '../ProcessedEntry/DomainEntry/processedTypes'
import { TimelineDiagram, browserStateMeta } from '../ProcessedEntry/DomainEntry/ProcessedEntryTimeline'
import { Widget } from '../Widget'
import { Chip } from '../Entries/autoDomainChips'
import { apiGet } from '../Entries/entriesApi'
import { parseDbTimestamp } from '@/lib/dates'
import {
  type TimelineEvent, type TimelineState,
  SOURCE_META, describeTimelineEvent, dedupe, groupByDay, deriveMilestones, inFlightAction, actorLabel, dayLabel,
} from '../Entries/timelineTypes'

// One timeline for everything that happened to an entry: sourced, received,
// classified, its domain and WHOIS, every browser capture and probe, every
// action (planned, queued, sent, submitted, replied, resolved) and every
// email in and out. Reads the entry_timeline_event view through
// /actions/entry/:id/timeline; the same look as the Domain intelligence
// "Campaign status timeline" (milestone diagram + proportional axis), with
// the full event list underneath, filterable by source. The strip reads
// left to right in time; the list reads newest first (the operator's call,
// 2026-10-18: what happened last is what the detail is opened for).
//
// Self-contained by user_entry_id, so it sits on the case page and on the
// promoted candidate's detail alike. Polls while an action is in flight so
// a click on Report shows up here without a refresh.

const POLL_MS = 15000
const LIST_CAP = 60

export function EntryTimelineWidget({
  userEntryId,
  candidateId,
  theme,
  refreshKey,
  className = 'mb-5',
  label = 'Entry timeline',
  compact = false,
  onOpenAction,
  screenshotHref,
}: {
  /** The promoted entry: everything, including the candidate life. */
  userEntryId?: string
  /** A candidate that is not an entry yet: its own arms only (ignored when userEntryId is set). */
  candidateId?: string
  theme: ProcessedTheme
  refreshKey?: number
  className?: string
  label?: React.ReactNode
  /** No milestone strip, list only (row expansions). */
  compact?: boolean
  /** Where "Open action" goes; default scrolls to the Actions taken widget on the page. */
  onOpenAction?: (actionId: string, channel: string | null) => void
  /** Link builder for a capture's r2_key; without it no screenshot link is shown. */
  screenshotHref?: (r2Key: string) => string
}) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'
  const [state, setState] = useState<TimelineState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // The event list under the strip folds away, the same chevron as the
  // page's collapsible sections; the strip (milestones and axis) stays and
  // is what shows by default -- the list opens on demand.
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [showAll, setShowAll] = useState(false)

  const path = userEntryId ? `/actions/entry/${encodeURIComponent(userEntryId)}/timeline`
    : candidateId ? `/actions/candidate/${encodeURIComponent(candidateId)}/timeline` : null
  const load = useCallback(async (quiet = false) => {
    if (!path) { setError('No entry or candidate to show'); setLoading(false); return }
    if (!quiet) setLoading(true)
    const r = await apiGet<TimelineState>(path)
    if (r.success && r.data) { setState(r.data); setError(null) }
    else setError(r.error ?? 'Could not load the timeline')
    setLoading(false)
  }, [path])

  useEffect(() => { load() }, [load, refreshKey])

  const all = useMemo(() => dedupe(state?.events ?? []), [state])
  const inFlight = useMemo(() => all.some(inFlightAction), [all])
  useEffect(() => {
    if (!inFlight) return
    const id = setInterval(() => load(true), POLL_MS)
    return () => clearInterval(id)
  }, [inFlight, load])

  const filtered = all
  const milestones = useMemo(() => deriveMilestones(all), [all])

  const dated = useMemo(() => all.map((e) => ({ e, d: parseDbTimestamp(e.at) })).filter((x): x is { e: TimelineEvent; d: Date } => x.d != null), [all])
  // The axis spans the events only: a domain registered years ago is a
  // milestone on the strip, not a reason to squeeze every event into the
  // right-hand edge.
  const minTs = dated.length ? Math.min(...dated.map((x) => x.d.getTime())) : 0
  const maxTs = dated.length ? Math.max(...dated.map((x) => x.d.getTime())) : 0
  const span = Math.max(maxTs - minTs, 1)

  const latestCapture = [...all].reverse().find((e) => e.kind === 'browser_capture')
  const latestMeta = latestCapture ? browserStateMeta(latestCapture.detail.state ?? 'unknown', theme) : null

  const visible = showAll || filtered.length <= LIST_CAP ? filtered : filtered.slice(filtered.length - LIST_CAP)
  // newest day first, newest event first within it: groupByDay keeps the
  // order it is given, so the reversed list groups the same way
  const groups = useMemo(() => groupByDay([...visible].reverse()), [visible])

  const openAction = (e: TimelineEvent) => {
    if (!e.action_id) return
    if (onOpenAction) return onOpenAction(e.action_id, e.channel)
    const target = (e.channel && document.querySelector<HTMLElement>(`[data-actions-widget] [data-channel="${e.channel}"]`)) || document.querySelector<HTMLElement>('[data-actions-widget]')
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      target.classList.add('ring-1', 'ring-ring')
      setTimeout(() => target.classList.remove('ring-1', 'ring-ring'), 1800)
    }
  }
  const openSteps = () => {
    const target = document.querySelector<HTMLElement>('[data-pipeline-step]')?.closest<HTMLElement>('[data-widget], section, div.rounded-[4px]') ?? document.querySelector<HTMLElement>('[data-pipeline-step]')
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const btn = `inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest transition ${c.line} ${c.muted} disabled:opacity-40`

  const headerBadges = (
    <div className="flex flex-wrap items-center gap-2">
      {latestMeta && (
        <span className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${latestMeta.border} ${latestMeta.bg} ${latestMeta.text}`}>Now {latestMeta.label}</span>
      )}
      {state && <span className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${c.line} ${c.soft} ${c.muted}`}>{all.length} events</span>}
      {state?.truncated && <span className="rounded border border-warning-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-warning">truncated</span>}
      {loading && <Loader2 className="h-3 w-3 animate-spin" />}
      <button type="button" onClick={() => load(true)} className={btn} title="Refresh"><RefreshCw className="h-3 w-3" /></button>
    </div>
  )

  const dateRange = dated.length ? `${timelineDateLabel(new Date(minTs), true)} → ${timelineDateLabel(new Date(maxTs), true)}` : ''

  return (
    <Widget
      label={<>{label}{dateRange ? <span className={`ml-3 tracking-normal font-normal ${c.muted}`}>{dateRange}</span> : null}</>}
      theme={theme}
      headerRight={headerBadges}
      className={className}
    >
      <div data-entry-timeline data-user-entry-id={userEntryId} data-candidate-id={userEntryId ? undefined : candidateId} className="flex flex-col gap-4">
        {error && <div className="font-mono text-[11px] text-destructive">{error}</div>}

        {loading && !state && (
          <div className={`py-6 text-center font-mono text-[11px] uppercase tracking-widest ${c.faint}`}>Loading the timeline…</div>
        )}
        {!loading && all.length === 0 && !error && (
          <div className={`py-6 text-center font-mono text-[11px] uppercase tracking-widest ${c.faint}`}>No events yet</div>
        )}

        {/* ── milestones + axis, the Domain intelligence look ─────────── */}
        {!compact && milestones.length > 0 && (
          <>
            <TimelineDiagram items={milestones} theme={theme} />
            <div className="px-2">
              <div className="relative h-12">
                <div className={`absolute left-0 right-0 top-5 h-px bg-muted`} />
                {dated.map(({ e, d }) => {
                  const left = Math.min(98, Math.max(2, ((d.getTime() - minTs) / span) * 100))
                  const desc = describeTimelineEvent(e)
                  const dot = e.kind === 'browser_capture' ? browserStateMeta(e.detail.state ?? 'unknown').dot : SOURCE_META[e.source].dot
                  return (
                    <div key={e.ref_id + e.kind} className="absolute top-2 -translate-x-1/2" style={{ left: `${left}%` }} title={`${desc.title} · ${timelineDateLabel(d, true)}`}>
                      <span className={`block h-4 w-4 rounded-full border-2 border-card ${dot} shadow-[0_0_0_1px_rgba(148,163,184,0.35)]`} />
                    </div>
                  )
                })}
              </div>
              <div className={`mt-1 flex justify-between font-mono text-[10px] ${c.faint}`}>
                <span>{timelineDateLabel(new Date(minTs), true)}</span>
                <span>{timelineDateLabel(new Date(maxTs), true)}</span>
              </div>
            </div>
          </>
        )}

        {/* ── the details: the event list, folded with a chevron ─────── */}
        {all.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-timeline-details-toggle>
            <button type="button" onClick={() => setDetailsOpen((v) => !v)} aria-expanded={detailsOpen}
              className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors hover:text-signal ${c.label}`}>
              <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${detailsOpen ? '' : '-rotate-90'}`} />
              Details · {all.length} events
            </button>
            <span className="flex-1" />
            {detailsOpen && filtered.length > LIST_CAP && (
              <button type="button" onClick={() => setShowAll((v) => !v)} className={btn}>
                {showAll ? `Show last ${LIST_CAP}` : `Show all ${filtered.length}`}
              </button>
            )}
          </div>
        )}

        {/* ── the event list, newest first, grouped by day ────────────── */}
        {detailsOpen && groups.length > 0 && (
          <ol className="flex flex-col gap-3" data-timeline-list>
            {groups.map((g) => (
              <li key={g.day}>
                <div className={`mb-1 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>{dayLabel(g.day)}</div>
                <ol className={`ml-1.5 border-l ${c.line}`}>
                  {g.events.map((e) => {
                    const desc = describeTimelineEvent(e)
                    const meta = SOURCE_META[e.source]
                    const Icon = meta.icon
                    const key = `${e.source}:${e.ref_id}:${e.kind}`
                    const isOpen = !!open[key]
                    const dot = e.kind === 'browser_capture' ? browserStateMeta(e.detail.state ?? 'unknown').dot : meta.dot
                    const who = actorLabel(e.actor)
                    return (
                      <li key={key} data-timeline-event data-source={e.source} data-kind={e.kind} className="relative pl-4 py-1">
                        <span className={`absolute -left-[5px] top-2.5 block h-2.5 w-2.5 rounded-full ${dot}`} />
                        <div className="flex flex-wrap items-center gap-2">
                          <button type="button" onClick={() => setOpen((o) => ({ ...o, [key]: !isOpen }))} className={`${c.faint} hover:${c.text}`} title="Details">
                            {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </button>
                          <span className={`font-mono text-[10px] ${c.faint}`}>{compactDate(e.at)}</span>
                          <Icon className={`h-3 w-3 ${c.faint}`} />
                          <span className={`font-mono text-[11px] ${c.text}`}>{desc.title}</span>
                          {desc.chip && <Chip label={desc.chip} tone={desc.tone} />}
                          <span className={`min-w-0 flex-1 truncate text-[11px] ${c.muted}`} title={desc.summary}>{desc.summary}</span>
                          {who && !desc.summary.includes(who) && <span className={`font-mono text-[10px] ${c.faint}`}>{who}</span>}
                          {e.action_id && (
                            <button type="button" data-action="timeline-open-action" data-action-id={e.action_id} onClick={() => openAction(e)} className={`${btn} hover:text-signal hover:border-signal`}>Open action</button>
                          )}
                          {e.source === 'detection' && <button type="button" onClick={openSteps} className={`${btn} hover:text-signal hover:border-signal`}>Steps</button>}
                          {e.kind === 'browser_capture' && e.detail.r2_key && screenshotHref && (
                            <a href={screenshotHref(e.detail.r2_key)} target="_blank" rel="noreferrer" className={`${btn} hover:text-signal hover:border-signal`}>Screenshot</a>
                          )}
                        </div>
                        {isOpen && (
                          <pre className={`mt-1 ml-5 max-h-48 overflow-auto whitespace-pre-wrap rounded border p-2 font-mono text-[10px] ${c.line} bg-card text-muted-foreground`}>
                            {JSON.stringify({ kind: e.kind, at: e.at, actor: e.actor, channel: e.channel, action_id: e.action_id, ref_id: e.ref_id, detail: e.detail }, null, 2)}
                          </pre>
                        )}
                      </li>
                    )
                  })}
                </ol>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Widget>
  )
}
